import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getDatabase, ref, set, onValue, push, get, runTransaction, query, orderByChild, equalTo, startAt, endAt } from 'firebase/database';
import { useFlutterwave } from 'flutterwave-react-v3';
import { useAuth } from '../context/AuthContext';
import PaymentReceipt from './PaymentReceipt.tsx';
import { schoolDetails as fallbackSchoolInfo } from '../schoolInfo';
import './Payment.css';
import { FaWallet, FaCreditCard, FaSpinner, FaTimes, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaCashRegister, FaSearch } from 'react-icons/fa';
import logo from '../pages/logo.png';

const Toast = ({ message, type, onRemove }) => {
    const renderIcon = () => {
        switch (type) {
            case 'success': return <FaCheckCircle />;
            case 'error': return <FaExclamationTriangle />;
            default: return <FaInfoCircle />;
        }
    };
    return (
        <div className={`toast toast-${type}`}>
            {renderIcon()} <p>{message}</p>
            <button onClick={onRemove} className="btn-close-toast"><FaTimes /></button>
        </div>
    );
};

const StableFlutterwavePayButton = ({ config: propConfig, disabled, processing, onSuccess, onClose, showToast, children }) => {
    const [config, setConfig] = useState(null);
    useEffect(() => {
        if (propConfig && propConfig.amount > 0 && propConfig.customer?.email) {
            setConfig(propConfig);
        } else {
            setConfig(null);
        }
    }, [propConfig]);
    const handleFlutterwavePayment = useFlutterwave(config);
    const handleClick = () => {
        if (!config) {
            showToast('Payment details incomplete. Please fill all fields.', 'error');
            return;
        }
        handleFlutterwavePayment({ callback: onSuccess, onClose });
    };
    return (
        <button type="button" className="btn btn-primary btn-lg btn-block" disabled={disabled || processing || !config} onClick={handleClick}>
            {children}
        </button>
    );
};

const SUPPORT_FEE = 100;
const VAT_RATE = 0.075;
const FLUTTERWAVE_PUBLIC_KEY = process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY;

const feeTypes = {
  school_fees: { label: "School Fees" },
  acceptance_fee: { label: "Acceptance Fee", amount: 1000 },
  mock_fee_sss2: { label: "Mock Fee (SSS 2)", amount: 15000, forClass: 'SSS 2' },
  junior_waec_jss3: { label: "Junior WAEC Fee (JSS 3)", amount: 25000, forClass: 'JSS 3' },
  waec_fee_sss3: { label: "WAEC Fee (SSS 3)", amount: 30000, forClass: 'SSS 3' },
  neco_fee_sss3: { label: "NECO Fee (SSS 3)", amount: 28000, forClass: 'SSS 3' },
  jamb_fee_sss3: { label: "JAMB Fee (SSS 3)", amount: 10000, forClass: 'SSS 3' },
  nabtech_fee_sss3: { label: "NABTECH Fee (SSS 3)", amount: 35000, forClass: 'SSS 3' },
  wassce_fee_sss3: { label: "WASSCE Private (SSS 3)", amount: 45000, forClass: 'SSS 3' },
  logistics: { label: "Logistics" },
  other: { label: "Other Fees" },
};

const calculateFlutterwaveFee = (amount) => {
  const fee = (1.4 / 100) * amount;
  return amount > 2500 ? fee + 100 : fee;
};

const calculateWalletFundingTotal = (baseAmount) => {
    if (baseAmount <= 0) return { total: 0, transactionFee: 0 };
    let transactionFee = 0;
    if (baseAmount >= 1000 && baseAmount <= 5000) {
        transactionFee = 200;
    } else if (baseAmount > 5000) {
        transactionFee = 400;
    }
    return { total: baseAmount + transactionFee, transactionFee };
};

const calculateStandardPaymentTotal = (baseAmount, paymentMethod) => {
    if (baseAmount <= 0) return { total: 0, vat: 0, flutterwaveFee: 0, supportFee: 0 };
    const vat = baseAmount * VAT_RATE;
    let flutterwaveFee = 0;
    if (paymentMethod === 'flutterwave') {
        const amountForFW = baseAmount + vat + SUPPORT_FEE;
        flutterwaveFee = calculateFlutterwaveFee(amountForFW);
    }
    const total = baseAmount + vat + SUPPORT_FEE + flutterwaveFee;
    return { total: Math.ceil(total), vat, flutterwaveFee, supportFee: SUPPORT_FEE };
};

const Payment = () => {
    const { currentUser, userData } = useAuth();
    const location = useLocation(); // <-- Add useLocation hook
    const userRole = userData?.role?.toLowerCase() || '';
    const isStudent = useMemo(() => userRole === 'student', [userRole]);
    const isParent = useMemo(() => userRole === 'parent', [userRole]);
    const isCashier = useMemo(() => userRole === 'cashier' || userRole === 'admin', [userRole]); // Admin can also be cashier

    const [students, setStudents] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [studentId, setStudentId] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [baseAmount, setBaseAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(isCashier ? 'cash' : 'wallet');
    const [paymentPurpose, setPaymentPurpose] = useState('school_fees');
    const [walletBalance, setWalletBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [calculatedFees, setCalculatedFees] = useState({ total: 0 });
    const [toasts, setToasts] = useState([]);
    const [showReceipt, setShowReceipt] = useState(false);
    const [currentReceipt, setCurrentReceipt] = useState(null);
    const [isWalletFunding, setIsWalletFunding] = useState(false);
    const [flutterwaveConfig, setFlutterwaveConfig] = useState(null);

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev.slice(-4), { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    }, []);

    // Effect to handle userId from URL for cashiers/admins
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userIdFromUrl = params.get('userId');

        if (userIdFromUrl && isCashier) {
            setLoading(true);
            const db = getDatabase();
            const userRef = ref(db, `users/${userIdFromUrl}`);
            get(userRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const userData = { id: snapshot.key, ...snapshot.val() };
                    if (userData.role === 'student') {
                        setStudents([userData]);
                        setStudentId(userData.id);
                        setSearchQuery(`${userData.fullName} (${userData.regNo})`);
                    }
                }
                setLoading(false);
            }).catch(() => setLoading(false));
        }
    }, [location.search, isCashier]);


    useEffect(() => {
        if (!currentUser) { setLoading(false); return; }
        const db = getDatabase();
        setLoading(true);

        if (isParent || isStudent) {
            onValue(ref(db, `users/${currentUser.uid}/balance`), (snap) => setWalletBalance(snap.val() || 0));
        }
        
        const loadInitialData = async () => {
            try {
                if (isParent) {
                    const childrenSnap = await get(ref(db, `users/${currentUser.uid}/children`));
                    if (childrenSnap.exists()) {
                        const childIds = Object.keys(childrenSnap.val());
                        const childrenData = await Promise.all(childIds.map(id => get(ref(db, `users/${id}`))));
                        setStudents(childrenData.map(snap => ({ id: snap.key, ...snap.val() })));
                    }
                } else if (isStudent) {
                    const studentSnap = await get(ref(db, `users/${currentUser.uid}`));
                    if (studentSnap.exists()) {
                        setStudents([{ id: currentUser.uid, ...studentSnap.val() }]);
                        setStudentId(currentUser.uid);
                    }
                }
            } catch (error) {
                showToast('Failed to load student data.', 'error');
            } finally {
                setLoading(false);
            }
        };
        const params = new URLSearchParams(location.search);
        if (!isCashier || !params.get('userId')) {
             loadInitialData();
        } else {
            setLoading(false);
        }
    }, [currentUser, isParent, isStudent, isCashier, showToast, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userIdFromUrl = params.get('userId');
        if (isCashier && searchQuery.length > 2 && !userIdFromUrl) {
            const db = getDatabase();
            const usersRef = ref(db, 'users');

            const fetchData = async () => {
                try {
                    const nameQuery = query(usersRef, orderByChild('fullName'), startAt(searchQuery), endAt(searchQuery + '\uf8ff'));
                    const regNoQuery = query(usersRef, orderByChild('regNo'), equalTo(searchQuery));

                    const [nameSnap, regNoSnap] = await Promise.all([get(nameQuery), get(regNoQuery)]);

                    const results = {};
                    if (nameSnap.exists()) {
                        nameSnap.forEach(child => { results[child.key] = { id: child.key, ...child.val() }; });
                    }
                    if (regNoSnap.exists()) {
                        regNoSnap.forEach(child => { results[child.key] = { id: child.key, ...child.val() }; });
                    }

                    setStudents(Object.values(results).filter(user => user.role === 'student'));
                } catch (error) {
                    showToast('Error searching students.', 'error');
                }
            };

            fetchData();
        }
    }, [searchQuery, isCashier, showToast, location.search]);

    useEffect(() => {
        setSelectedStudent(students.find(s => s.id === studentId) || null);
    }, [studentId, students]);

    useEffect(() => {
        const fee = feeTypes[paymentPurpose];
        setBaseAmount(fee?.amount ? fee.amount.toString() : '');
    }, [paymentPurpose]);

    useEffect(() => {
        const amountNum = parseFloat(baseAmount) || 0;
        if (isWalletFunding) {
            setCalculatedFees(calculateWalletFundingTotal(amountNum));
        } else {
            setCalculatedFees(calculateStandardPaymentTotal(amountNum, paymentMethod));
        }
    }, [baseAmount, paymentMethod, isWalletFunding]);

    useEffect(() => {
        const finalAmount = calculatedFees.total;
        if (!currentUser || finalAmount <= 0) {
            setFlutterwaveConfig(null); return;
        }

        let customerEmail, customerName;
        if (isWalletFunding) {
            customerEmail = currentUser.email;
            customerName = userData.fullName;
        } else if (selectedStudent) {
            customerEmail = selectedStudent.email || currentUser.email;
            customerName = selectedStudent.fullName;
        } else {
            setFlutterwaveConfig(null); return;
        }

        setFlutterwaveConfig({
            public_key: FLUTTERWAVE_PUBLIC_KEY,
            tx_ref: `${isWalletFunding ? 'wallet' : paymentPurpose}-${Date.now()}`,
            amount: finalAmount,
            currency: 'NGN',
            payment_options: 'card,ussd,opay,banktransfer',
            customer: { email: customerEmail, name: customerName },
            meta: { userId: currentUser.uid, studentId: selectedStudent?.id, purpose: paymentPurpose, ...calculatedFees },
            customizations: { title: isWalletFunding ? 'Wallet Funding' : `Payment for ${customerName}`, description: feeTypes[paymentPurpose]?.label || 'School Payment', logo },
        });
    }, [calculatedFees, paymentPurpose, selectedStudent, currentUser, userData, isWalletFunding]);

    const createReceiptAndShow = useCallback(async (paymentData, paymentRef) => {
        const db = getDatabase();
        const schoolInfoSnap = await get(ref(db, 'school'));
        const schoolInfo = schoolInfoSnap.val() || fallbackSchoolInfo;
        const receiptData = { ...paymentData, receiptId: paymentRef.key, regNo: selectedStudent?.regNo, className: selectedStudent?.className, ...schoolInfo, cashierName: isCashier ? userData.fullName : 'Self-Service' };
        setCurrentReceipt(receiptData);
        setShowReceipt(true);
    }, [selectedStudent, isCashier, userData]);

    const handlePaymentSuccess = useCallback(async (response) => {
        setProcessing(true);
        const db = getDatabase();
        try {
            const amountNum = parseFloat(baseAmount);
            if (isWalletFunding) {
                await runTransaction(ref(db, `users/${currentUser.uid}/balance`), (bal) => (bal || 0) + amountNum);
                showToast(`Wallet funded with ₦${amountNum.toLocaleString()}!`, 'success');
            } else {
                const paymentRef = push(ref(db, `users/${studentId}/payments`));
                const paymentData = { studentId, studentName: selectedStudent?.fullName, amount: amountNum, ...calculatedFees, feeType: paymentPurpose, paymentMethod, date: new Date().toISOString(), referenceCode: response?.transaction_id || `CASH-${Date.now()}`, status: 'successful' };
                await set(paymentRef, paymentData);
                await createReceiptAndShow(paymentData, paymentRef);
                showToast('Payment recorded successfully!', 'success');
            }
            setBaseAmount('');
            setSearchQuery('');
            setStudentId('');
        } catch (error) {
            showToast(`An error occurred: ${error.message}`, 'error');
        } finally {
            setProcessing(false);
        }
    }, [isWalletFunding, baseAmount, calculatedFees, currentUser, studentId, selectedStudent, paymentMethod, paymentPurpose, showToast, createReceiptAndShow]);

    const handleWalletPayment = useCallback(async () => {
        const db = getDatabase();
        const { total } = calculatedFees;
        if (total <= 0 || !studentId) { showToast('Invalid student or amount.', 'error'); return; }
        if (walletBalance < total) { showToast('Insufficient wallet balance.', 'error'); return; }
        setProcessing(true);
        try {
            await runTransaction(ref(db, `users/${currentUser.uid}/balance`), (bal) => {
                if ((bal || 0) < total) throw new Error('Insufficient funds.');
                return (bal || 0) - total;
            });
            await handlePaymentSuccess({ transaction_id: `WALLET-${Date.now()}` });
        } catch (error) {
            showToast(error.message, 'error');
            setProcessing(false);
        }
    }, [calculatedFees, studentId, walletBalance, currentUser, handlePaymentSuccess, showToast]);
    
    const handleCashPayment = useCallback(async () => {
         if (calculatedFees.total <= 0 || !studentId) { showToast('Invalid student or amount.', 'error'); return; }
         await handlePaymentSuccess({ transaction_id: `CASH-${Date.now()}` });
    }, [calculatedFees, studentId, handlePaymentSuccess, showToast]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (paymentMethod === 'wallet') handleWalletPayment();
        else if (paymentMethod === 'cash') handleCashPayment();
    }, [paymentMethod, handleWalletPayment, handleCashPayment]);

    if (loading) return <div className="text-center"><div className="spinner-border" /></div>;
    if (showReceipt) return <PaymentReceipt receipt={currentReceipt} onClose={() => setShowReceipt(false)} />;

    const renderFeeTypeOptions = () => {
        const studentClass = selectedStudent?.className;
        return Object.entries(feeTypes).map(([key, fee]) => {
            if (fee.forClass && fee.forClass !== studentClass) return null;
            return <option key={key} value={key}>{fee.label}</option>;
        }).filter(Boolean);
    };
    
    return (
        <div className="payment-container">
            <div className="toast-container-wrapper">{toasts.map(t => <Toast key={t.id} {...t} onRemove={() => setToasts(p => p.filter(toast => toast.id !== t.id))} />)}</div>
            {!isCashier && <aside className="sidebar-column">
                <div className="wallet-card"><div className="wallet-header"><FaWallet /><h3>Wallet Balance</h3></div><div className="wallet-balance">₦{walletBalance.toLocaleString()}</div><button className="btn btn-primary" onClick={() => { setIsWalletFunding(true); setPaymentMethod('flutterwave'); }}>Fund Wallet</button></div>
            </aside>}
            <main>
                <form onSubmit={handleSubmit} className="payment-form">
                    <h3>{isWalletFunding ? 'Fund Your Wallet' : (isCashier ? 'Log Student Payment' : 'Make a Payment')}</h3>
                    {!isWalletFunding && <div className="payment-method-tabs">
                        {isCashier ? <button type='button' className={`tab active`}><FaCashRegister /> Log Offline Payment</button> : <>
                            <button type='button' className={`tab ${paymentMethod === 'wallet' ? 'active' : ''}`} onClick={() => setPaymentMethod('wallet')}><FaWallet /> Wallet</button>
                            <button type='button' className={`tab ${paymentMethod === 'flutterwave' ? 'active' : ''}`} onClick={() => setPaymentMethod('flutterwave')}><FaCreditCard /> Online</button>
                        </>}
                    </div>}
                    {!isWalletFunding && (
                         <div className="form-group">
                            <label>Student</label>
                            {isCashier && <div className="input-group mb-3"><span className="input-group-text"><FaSearch/></span><input type="text" className="form-control" placeholder="Search by name or Reg No..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div>}
                            <select className="form-select" value={studentId} onChange={e => setStudentId(e.target.value)} required disabled={isStudent || (isCashier && new URLSearchParams(location.search).has('userId'))}><option value="">{isStudent ? (students[0]?.fullName || '...') : (isCashier && !searchQuery ? 'Select a student below' : (isCashier ? 'Select a student from search results' : 'Select your child'))}</option>{students.map(s => <option key={s.id} value={s.id}>{s.fullName} ({s.regNo})</option>)}</select>
                        </div>
                    )}
                    {!isWalletFunding && selectedStudent && <div className="form-group"><label>Payment For</label><select className="form-select" value={paymentPurpose} onChange={e => setPaymentPurpose(e.target.value)} required>{renderFeeTypeOptions()}</select></div>}
                    <div className="form-group"><label>{isWalletFunding ? 'Amount to Fund' : 'Base Amount'} (₦)</label><input type="number" className="form-control" value={baseAmount} onChange={e => setBaseAmount(e.target.value)} required min="1" placeholder={'Enter amount'} disabled={!!feeTypes[paymentPurpose]?.amount && !isWalletFunding} /></div>
                    {baseAmount > 0 && (
                        <div className="fee-breakdown">
                            {isWalletFunding ? <>
                                <p>Funding Amount: <span>₦{parseFloat(baseAmount).toLocaleString()}</span></p>
                                {calculatedFees.transactionFee > 0 && <p>Transaction Fee: <span>₦{calculatedFees.transactionFee.toLocaleString()}</span></p>}
                                <hr/>
                                <p className="total-due">Total to Pay: <span>₦{calculatedFees.total.toLocaleString()}</span></p>
                            </> : <>
                                <p>Base Amount: <span>₦{parseFloat(baseAmount).toLocaleString()}</span></p>
                                <p>VAT (7.5%): <span>₦{calculatedFees.vat.toLocaleString()}</span></p>
                                <p>Support Fee: <span>₦{calculatedFees.supportFee.toLocaleString()}</span></p>
                                {paymentMethod === 'flutterwave' && <p>Online Fee: <span>₦{calculatedFees.flutterwaveFee.toLocaleString()}</span></p>}
                                <hr />
                                <p className="total-due">Total Due: <span>₦{calculatedFees.total.toLocaleString()}</span></p>
                            </>}
                        </div>
                    )}
                    <div className="mt-4">
                        {paymentMethod === 'flutterwave' ? <StableFlutterwavePayButton config={flutterwaveConfig} onSuccess={handlePaymentSuccess} onClose={() => showToast('Payment cancelled', 'info')} disabled={processing} processing={processing} showToast={showToast}>{processing ? <><FaSpinner className="spinner"/> Processing...</> : `Pay ₦${calculatedFees.total.toLocaleString()}`}</StableFlutterwavePayButton> : (paymentMethod === 'wallet' ? <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={processing || !studentId || !baseAmount || walletBalance < calculatedFees.total}>{processing ? <><FaSpinner className="spinner"/> Processing...</> : `Pay ₦${calculatedFees.total.toLocaleString()} from Wallet`}</button> : <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={processing || !studentId || !baseAmount}>{processing ? <><FaSpinner className="spinner"/> Logging...</> : `Log Payment of ₦${calculatedFees.total.toLocaleString()}`}</button>)}
                    </div>
                    {!isCashier && <button type="button" className="btn btn-link" onClick={() => setIsWalletFunding(!isWalletFunding)}>{isWalletFunding ? 'Back to Fee Payment' : 'Want to fund your wallet instead?'}</button>}
                </form>
            </main>
        </div>
    );
};

export default Payment;